import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, Box } from '@material-ui/core';
import { getThemeColors } from '../utils/themeUtils';

const useStyles = makeStyles(theme => ({
	container: {
		backgroundColor: '#1e293b',
		borderRadius: '8px',
		border: '1px solid rgba(255, 255, 255, 0.1)',
		overflow: 'hidden',
		boxShadow: 'none',
		width: '100%',
		boxSizing: 'border-box'
	},
	gaugesGrid: {
		display: 'grid',
		gridTemplateColumns: '1fr',
		[theme.breakpoints.up('sm')]: {
			gridTemplateColumns: 'repeat(2, 1fr)',
			'& > div:first-child': {
				borderRight: '1px solid rgba(255, 255, 255, 0.1)'
			}
		}
	},
	gaugeCard: {
		padding: '24px',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		minWidth: 0
	},
	gaugeContainer: {
		position: 'relative',
		width: 120,
		height: 120,
		marginBottom: theme.spacing(2)
	},
	gaugeSvg: {
		transform: 'rotate(-90deg)',
		width: '100%',
		height: '100%'
	},
	gaugeBackground: {
		fill: 'none',
		stroke: '#334155',
		strokeWidth: 8
	},
	gaugeForeground: {
		fill: 'none',
		strokeWidth: 8,
		strokeLinecap: 'round',
		transition: 'stroke-dashoffset 0.5s ease'
	},
	gaugeText: {
		position: 'absolute',
		top: '50%',
		left: '50%',
		transform: 'translate(-50%, -50%)',
		textAlign: 'center'
	},
	gaugeValue: {
		fontSize: '28px',
		fontWeight: 700,
		color: '#f8fafc',
		lineHeight: 1
	},
	gaugeUnit: {
		fontSize: '12px',
		color: '#94a3b8',
		marginTop: '4px'
	},
	gaugeLabel: {
		fontSize: '14px',
		fontWeight: 600,
		color: '#cbd5e1',
		textAlign: 'center'
	},
	gaugeSubtext: {
		fontSize: '12px',
		color: '#64748b',
		textAlign: 'center',
		marginTop: '4px'
	}
}));

const CircularGauge = ({ value, maxValue, color, label, subtext, unit = '%', colors }) => {
	const classes = useStyles();
	const percentage = Math.min((value / maxValue) * 100, 100);
	const radius = 52;
	const circumference = 2 * Math.PI * radius;
	const offset = circumference - (percentage / 100) * circumference;

	return (
		<div className={classes.gaugeCard}>
			<div className={classes.gaugeContainer}>
				<svg className={classes.gaugeSvg}>
					<circle
						className={classes.gaugeBackground}
						cx="60"
						cy="60"
						r={radius}
						style={{ stroke: colors.background.tertiary }}
					/>
					<circle
						className={classes.gaugeForeground}
						cx="60"
						cy="60"
						r={radius}
						stroke={color}
						strokeDasharray={circumference}
						strokeDashoffset={offset}
					/>
				</svg>
				<div className={classes.gaugeText}>
					<div className={classes.gaugeValue} style={{ color: colors.text.primary }}>
						{typeof value === 'number' ? (value % 1 === 0 ? value.toFixed(0) : value.toFixed(1)) : value}
					</div>
					<div className={classes.gaugeUnit} style={{ color: colors.text.muted }}>{unit}</div>
				</div>
			</div>
			<Typography className={classes.gaugeLabel} style={{ color: colors.text.secondary }}>{label}</Typography>
			{subtext && (
				<Typography className={classes.gaugeSubtext} style={{ color: colors.text.tertiary }}>{subtext}</Typography>
			)}
		</div>
	);
};

const MetricGauges = ({ metrics, themeMode = 'dark' }) => {
	const classes = useStyles();
	const colors = getThemeColors(themeMode);

	if (!metrics) return null;

	const totalResources = (metrics.totalGPUs || 0) + (metrics.totalNodes || 0);
	const avgResourceUtilization = totalResources > 0
		? ((metrics.avgGpuUtilization || 0) + (metrics.avgCpuUtilization || 0)) / 2
		: 0;

	return (
		<div 
			className={classes.container}
			style={{
				backgroundColor: colors.card.background,
				border: `1px solid ${colors.card.border}`,
				boxShadow: colors.card.shadow
			}}
		>
			<div 
				className={classes.gaugesGrid}
				style={{
					'& > div:first-child': { borderColor: colors.border.primary }
				}}
			>
			{/* Overall Resource Utilization */}
			<CircularGauge
				value={avgResourceUtilization}
				maxValue={100}
				color={colors.accent.blue}
				label="Resource Utilization"
				subtext={`${totalResources} total resources`}
				unit="%"
				colors={colors}
			/>				{/* Cluster Health */}
				<CircularGauge
					value={(metrics.activeClusters || 0)}
					maxValue={(metrics.totalClusters || 1)}
					color={colors.accent.green}
					label="Active Clusters"
					subtext={`${metrics.totalClusters || 0} total clusters`}
					unit={`/${metrics.totalClusters || 0}`}
					colors={colors}
				/>
			</div>
		</div>
	);
};

// Memoize to prevent unnecessary re-renders when metrics haven't changed
export default React.memo(MetricGauges, (prevProps, nextProps) => {
	return prevProps.themeMode === nextProps.themeMode &&
		JSON.stringify(prevProps.metrics) === JSON.stringify(nextProps.metrics);
});

